<template>
  <div class="device-detail-container" v-loading="loading">
    <div class="control-panel">
      <div class="control-container">
        <div class="header-info">
          <i class="el-icon-cpu"></i>
          <span>{{ $t('device.deviceDetail') }}</span>
        </div>
        <div class="action-wrapper">
          <el-button @click="goBack" size="small">
            <i class="el-icon-back"></i> {{ $t('common.back') }}
          </el-button>
        </div>
      </div>
    </div>

    <!-- 设备信息展示 -->
    <div class="detail-content">
      <el-row :gutter="20">
        <!-- 左侧基本信息与技术参数 -->
        <el-col :xs="24" :lg="16">
          <el-card class="detail-card info-main-card" shadow="hover">
            <div slot="header" class="card-header">
              <span><i class="el-icon-cpu"></i> {{ $t('device.deviceBasicInfo') }}</span>
            </div>
            
            <el-descriptions :column="2" border>
              <el-descriptions-item :label="$t('device.deviceCode')">
                <span class="info-value highlighted">{{ deviceDetail.deviceCode }}</span>
              </el-descriptions-item>
              <el-descriptions-item :label="$t('device.macAddress')">
                <span class="info-value highlighted">{{ deviceDetail.macAddress }}</span>
              </el-descriptions-item>
              <el-descriptions-item :label="$t('device.deviceName')">
                <span class="info-value">{{ deviceDetail.deviceName }}</span>
              </el-descriptions-item>
              <el-descriptions-item :label="$t('device.deviceModel')">
                  <el-tag size="small" effect="plain">{{ deviceDetail.boardLabel || getDeviceTypeLabel(deviceDetail.board) }}</el-tag>
                </el-descriptions-item>
                <el-descriptions-item :label="$t('device.firmwareVersion')">
                  <el-tag size="small" type="info">{{ deviceDetail.appVersion }}</el-tag>
                </el-descriptions-item>
              <el-descriptions-item :label="$t('device.ownerName')">
                <span>{{ deviceDetail.ownerName || '-' }}</span>
              </el-descriptions-item>
              <el-descriptions-item :label="$t('device.agentId')">
                <span>{{ deviceDetail.agentId || '-' }}</span>
              </el-descriptions-item>
              <el-descriptions-item :label="$t('device.autoUpdate')">
                  <el-tag :type="deviceDetail.autoUpdate === 1 ? 'success' : 'info'" size="small">
                    {{ deviceDetail.autoUpdate === 1 ? $t('common.enabled') : $t('common.disabled') }}
                  </el-tag>
                </el-descriptions-item>
                <el-descriptions-item label="设备音量">
                  <div class="volume-display" v-if="deviceDetail.volume !== null && deviceDetail.volume !== undefined">
                    <el-progress
                      :percentage="deviceDetail.volume"
                      :color="getVolumeColor(deviceDetail.volume)"
                      :stroke-width="14"
                      :text-inside="true"
                      style="width: 120px"
                    />
                    <span class="volume-label">{{ deviceDetail.volume }}%</span>
                  </div>
                  <span v-else>-</span>
                </el-descriptions-item>
                <el-descriptions-item label="WIFI名称">
                  <span>{{ deviceDetail.wifiSsid || '-' }}</span>
                </el-descriptions-item>
                <el-descriptions-item :label="$t('device.activationTime')">
                <span>{{ formatDateTime(deviceDetail.activationTime) || '-' }}</span>
              </el-descriptions-item>
              <el-descriptions-item :label="$t('device.createdAt')">
                <span>{{ formatDateTime(deviceDetail.createDate) }}</span>
              </el-descriptions-item>
              <el-descriptions-item :label="$t('device.updatedAt')">
                <span>{{ formatDateTime(deviceDetail.updateDate) }}</span>
              </el-descriptions-item>
              <el-descriptions-item :label="$t('device.lastOnlineTime')">
                <span>{{ formatDateTime(deviceDetail.lastConnectedAt) || '-' }}</span>
              </el-descriptions-item>
              <el-descriptions-item v-if="deviceDetail.recentChatTime" :label="$t('device.lastConversation')">
                <span>{{ deviceDetail.recentChatTime }}</span>
              </el-descriptions-item>
              <el-descriptions-item :label="$t('device.creator')">
                <span class="text-secondary">{{ deviceDetail.creatorName || deviceDetail.creator || '-' }}</span>
              </el-descriptions-item>
              <el-descriptions-item :label="$t('device.updater')">
                <span class="text-secondary">{{ deviceDetail.updaterName || deviceDetail.updater || '-' }}</span>
              </el-descriptions-item>
            </el-descriptions>
          </el-card>

          <!-- 日志摘要 -->
          <el-card class="detail-card mt-20" shadow="hover">
            <div slot="header" class="card-header">
              <span><i class="el-icon-document"></i> {{ $t('device.recentLogs') }}</span>
              <el-button type="text" @click="handleViewLogs">{{ $t('common.viewMore') }}</el-button>
            </div>
            <el-table :data="recentLogs" size="small" height="300" style="width: 100%">
              <el-table-column prop="timestamp" :label="$t('common.time')" width="160">
                <template slot-scope="scope">{{ formatDateTime(scope.row.timestamp) }}</template>
              </el-table-column>
              <el-table-column prop="type" :label="$t('device.logType')" width="100">
                <template slot-scope="scope">
                  <el-tag :type="getLogTypeColor(scope.row.type)" size="mini">{{ getLogTypeLabel(scope.row.type) }}</el-tag>
                </template>
              </el-table-column>
              <el-table-column prop="description" :label="$t('device.logDescription')" show-overflow-tooltip></el-table-column>
            </el-table>
          </el-card>
        </el-col>

        <!-- 右侧状态与操作 -->
        <el-col :xs="24" :lg="8">
          <el-card class="detail-card status-card" shadow="hover">
            <div slot="header" class="card-header">
              <span><i class="el-icon-odometer"></i> {{ $t('device.statusInfo') }}</span>
              <el-button type="text" icon="el-icon-refresh" @click="refreshStatus">{{ $t('common.refresh') }}</el-button>
            </div>
            
            <div class="status-list">
              <div class="status-item">
                <span class="label">{{ $t('device.onlineStatus') }}</span>
                <el-tag :type="deviceDetail.status === 1 ? 'success' : 'danger'" effect="dark">
                  {{ deviceDetail.status === 1 ? $t('common.online') : $t('common.offline') }}
                </el-tag>
              </div>
              <div class="status-item mt-10">
                <span class="label">{{ $t('device.bindingStatus') }}</span>
                <el-tag :type="deviceDetail.bound ? 'success' : 'info'" effect="dark">
                  {{ deviceDetail.bound ? $t('device.bound') : $t('device.unbound') }}
                </el-tag>
              </div>
              <div class="status-item mt-10" v-if="deviceDetail.bound && hasPermission('sys:device:update')">
                <el-button type="danger" plain size="small" icon="el-icon-delete" @click="handleUnbindDevice">
                  {{ $t('device.unbindDevice') }}
                </el-button>
              </div>
            </div>
          </el-card>


          <el-card class="detail-card mt-20" shadow="hover">
            <div slot="header" class="card-header">
              <span><i class="el-icon-notebook-2"></i> {{ $t('device.deviceRemark') }}</span>
              <el-button type="text" @click="handleEditRemark">{{ remarkEditing ? '' : $t('common.edit') }}</el-button>
            </div>
            <div v-if="!remarkEditing" class="remark-text">{{ deviceDetail.deviceRemark || $t('common.noData') }}</div>
            <div v-else>
              <el-input type="textarea" v-model="deviceDetail.deviceRemark" :rows="4" />
              <div class="remark-actions mt-10">
                <el-button size="mini" @click="cancelRemarkEdit">{{ $t('common.cancel') }}</el-button>
                <el-button size="mini" type="primary" @click="saveRemark">{{ $t('common.save') }}</el-button>
              </div>
            </div>
          </el-card>
        </el-col>
      </el-row>
    </div>

    <!-- 固件升级对话框 -->
    <el-dialog
      :title="$t('device.firmwareUpgrade')"
      :visible.sync="firmwareDialog.visible"
      width="600px"
      :close-on-click-modal="false"
    >
      <div class="firmware-upgrade-content">
        <p>{{ $t('device.firmwareUpgradeDesc') }}</p>
        
        <el-form :model="firmwareForm" label-width="120px">
          <el-form-item :label="$t('device.currentVersion')">
            <el-input v-model="deviceDetail.appVersion" disabled />
          </el-form-item>
          
          <el-form-item :label="$t('device.targetVersion')" required>
            <el-select v-model="firmwareForm.targetVersion" :placeholder="$t('device.selectVersion')">
              <el-option v-for="version in availableVersions" :key="version" :label="version" :value="version"></el-option>
            </el-select>
          </el-form-item>
          
          <el-form-item :label="$t('device.upgradeNotes')">
            <el-input
              type="textarea"
              v-model="firmwareForm.notes"
              :rows="3"
              :placeholder="$t('device.upgradeNotesPlaceholder')"
            />
          </el-form-item>
        </el-form>

        <!-- 升级进度 -->
        <div v-if="firmwareDialog.progress > 0" class="upgrade-progress">
          <h4>{{ $t('device.upgradeProgress') }}</h4>
          <el-progress :percentage="firmwareDialog.progress" :status="firmwareDialog.progress === 100 ? 'success' : ''" />
        </div>
      </div>
      
      <div slot="footer" class="dialog-footer">
        <el-button @click="firmwareDialog.visible = false">{{ $t('common.cancel') }}</el-button>
        <el-button 
          type="primary" 
          @click="executeFirmwareUpgrade" 
          :loading="firmwareDialog.loading"
          :disabled="!firmwareForm.targetVersion"
        >
          {{ $t('device.startUpgrade') }}
        </el-button>
      </div>
    </el-dialog>

    <!-- 设备日志对话框 -->
    <el-dialog
      :title="$t('device.deviceLogs')"
      :visible.sync="logsDialog.visible"
      width="800px"
      :close-on-click-modal="false"
    >
      <div class="device-logs-content">
        <el-form :model="logsForm" :inline="true" label-position="left" label-width="80px">
          <el-form-item :label="$t('device.logType')">
            <el-select v-model="logsForm.logType" clearable>
              <el-option :label="$t('device.allLogs')" value=""></el-option>
              <el-option :label="$t('device.operationLog')" value="operation"></el-option>
              <el-option :label="$t('device.statusLog')" value="status"></el-option>
              <el-option :label="$t('device.errorLog')" value="error"></el-option>
            </el-select>
          </el-form-item>
          
          <el-form-item :label="$t('common.date')">
            <el-date-picker
              v-model="logsForm.dateRange"
              type="daterange"
              :range-separator="$t('common.to')"
              :start-placeholder="$t('common.startDate')"
              :end-placeholder="$t('common.endDate')"
              format="yyyy-MM-dd"
              value-format="yyyy-MM-dd"
            />
          </el-form-item>
          
          <el-form-item>
            <el-button type="primary" @click="fetchDeviceLogs">{{ $t('common.search') }}</el-button>
            <el-button @click="resetLogsForm">{{ $t('common.reset') }}</el-button>
          </el-form-item>
        </el-form>

        <el-table
          :data="deviceLogs"
          stripe
          border
          v-loading="logsDialog.loading"
          :element-loading-text="$t('common.loading')"
          style="margin-top: 20px;"
        >
          <el-table-column prop="timestamp" :label="$t('common.time')" width="160">
            <template slot-scope="scope">
              {{ formatDateTime(scope.row.timestamp) }}
            </template>
          </el-table-column>
          <el-table-column prop="type" :label="$t('device.logType')" width="100">
            <template slot-scope="scope">
              <el-tag :type="getLogTypeColor(scope.row.type)" size="small">
                {{ getLogTypeLabel(scope.row.type) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="action" :label="$t('device.logAction')" width="120"></el-table-column>
          <el-table-column prop="description" :label="$t('device.logDescription')"></el-table-column>
          <el-table-column prop="operator" :label="$t('device.logOperator')" width="100"></el-table-column>
        </el-table>

        <div class="pagination-container">
          <el-pagination
            @size-change="handleLogsSizeChange"
            @current-change="handleLogsCurrentChange"
            :current-page="logsPagination.currentPage"
            :page-sizes="[10, 20, 50]"
            :page-size="logsPagination.pageSize"
            layout="total, sizes, prev, pager, next, jumper"
            :total="logsPagination.total"
          />
        </div>
      </div>
    </el-dialog>

  </div>
</template>

<script>
import { getDeviceDetail, updateDevice, unbindDeviceByCode, upgradeFirmware, getFirmwareProgress, getDeviceLogs } from '@/apis/device/device'
import { getUserList } from '@/apis/user/user'
import { hasPermission } from '@/utils/permission'

export default {
  name: 'DeviceDetail',
  data() {
    return {
      deviceDetail: {
        id: '',
        deviceName: '',
        deviceCode: '',
        macAddress: '',
        board: '',
        appVersion: '',
        status: 0,
        lastConnectedAt: '',
        createDate: '',
        updateDate: '',
        deviceRemark: '',
        bound: false,
        agentId: '',
        autoUpdate: 0,
        creator: '',
        updater: '',
        creatorName: '',
        updaterName: '',
        sort: 0,
        ownerName: '',
        batchNo: '',
        lifecycleStatus: '',
        activationTime: ''
      },
      loading: false,
      remarkEditing: false,
      originalRemark: '',
      availableUsers: [],
      availableVersions: ['1.2.4', '1.2.5', '1.3.0'],
      firmwareTypes: [
        { key: 'esp32-c3', name: 'ESP32-C3' },
        { key: 'esp32-s3', name: 'ESP32-S3' },
        { key: 'esp32-v2', name: 'ESP32-V2' }
      ],
      deviceLogs: [],
      firmwareDialog: {
        visible: false,
        loading: false,
        progress: 0
      },
      logsDialog: {
        visible: false,
        loading: false
      },
      firmwareForm: {
        targetVersion: '',
        notes: ''
      },
      logsForm: {
        logType: '',
        dateRange: []
      },
      logsPagination: {
        currentPage: 1,
        pageSize: 20,
        total: 0
      }
    }
  },
  computed: {
    recentLogs() {
      return this.deviceLogs.slice(0, 5)
    }
  },
  created() {
    this.initData()
  },
  beforeDestroy() {
    // 清理固件升级进度监控定时器，防止组件销毁后定时器持续运行
    if (this._upgradeTimer) {
      clearInterval(this._upgradeTimer)
      this._upgradeTimer = null
    }
  },
  methods: {
    hasPermission,
    async initData() {
      // 并行获取设备详情和日志，提高首屏加载速度
      Promise.all([
        this.fetchDeviceDetail(),
        this.fetchDeviceLogs()
      ])
      
      // 后台异步获取可用用户列表，不阻塞核心信息的显示
      // 只有在点击“绑定设备”时才真正需要此数据
      this.fetchAvailableUsers()
    },

    // 获取设备型号名称
    getDeviceTypeLabel(type) {
      if (!type) return type
      const firmwareType = this.firmwareTypes.find(item => item.key === type)
      return firmwareType ? firmwareType.name : type
    },

    // 获取日志类型颜色
    getLogTypeColor(type) {
      const colorMap = {
        operation: 'primary',
        status: 'success',
        error: 'danger'
      }
      return colorMap[type] || 'info'
    },

    // 获取日志类型标签
    getLogTypeLabel(type) {
      const labelMap = {
        operation: this.$t('device.operationLog'),
        status: this.$t('device.statusLog'),
        error: this.$t('device.errorLog')
      }
      return labelMap[type] || this.$t('device.unknownLog')
    },

    // 获取生命周期状态标签
    getLifecycleStatusLabel(status) {
      if (!status) return '-'
      const statusMap = {
        'CREATED': this.$t('device.lifecycleCreated'),
        'PRODUCTION': this.$t('device.lifecycleProduction'),
        'WAREHOUSE': this.$t('device.lifecycleWarehouse'),
        'SHIPPED': this.$t('device.lifecycleShipped'),
        'ACTIVATED': this.$t('device.lifecycleActivated')
      }
      return statusMap[status] || status
    },

    // 获取生命周期状态颜色
    getLifecycleStatusType(status) {
      const typeMap = {
        'CREATED': 'info',
        'PRODUCTION': 'primary',
        'WAREHOUSE': 'warning',
        'SHIPPED': 'success',
        'ACTIVATED': 'success'
      }
      return typeMap[status] || 'info'
    },

    // 格式化日期时间
    formatDateTime(dateTime) {
      if (!dateTime) return ''
      return new Date(dateTime).toLocaleString()
    },

    // 获取音量进度条颜色
    getVolumeColor(volume) {
      if (volume === 0) return '#909399'
      if (volume <= 30) return '#67c23a'
      if (volume <= 70) return '#409eff'
      return '#e6a23c'
    },

    // 返回上一页
    goBack() {
      this.$router.back()
    },

    // 编辑设备
    handleEdit() {
      this.$router.push(`/device/edit/${this.$route.params.id}`)
    },

    // 获取设备详情
    async fetchDeviceDetail() {
      this.loading = true
      try {
        const deviceId = this.$route.params.id
        const data = await getDeviceDetail(deviceId)
        if (data) {
          this.deviceDetail = {
            ...this.deviceDetail,
            ...data,
            userId: data.userId || data.user_id,
            bound: !!(data.userId || data.user_id)
          }

          // 尝试从已获取的用户列表中匹配所有者、创建者和更新者名称
          this.resolveUserNames()
        }
      } catch (error) {
        console.error('获取设备详情失败:', error)
        this.$message.error(this.$t('device.getDeviceDetailFailed'))
      } finally {
        this.loading = false
      }
    },

    // 解析用户相关的名称（所有者、创建者、更新者）
    resolveUserNames() {
      if (this.availableUsers.length === 0) return

      const findUserName = (id) => {
        if (!id) return null
        const targetId = String(id)
        const user = this.availableUsers.find(u => String(u.id) === targetId)
        return user ? (user.username || user.name || user.id) : null
      }

      // 解析所有者名称
      if (this.deviceDetail.userId) {
        const ownerName = findUserName(this.deviceDetail.userId)
        if (ownerName) this.deviceDetail.ownerName = ownerName
      }

      // 解析创建者名称
      if (this.deviceDetail.creator) {
        const creatorName = findUserName(this.deviceDetail.creator)
        if (creatorName) this.deviceDetail.creatorName = creatorName
      }

      // 解析更新者名称
      if (this.deviceDetail.updater) {
        const updaterName = findUserName(this.deviceDetail.updater)
        if (updaterName) this.deviceDetail.updaterName = updaterName
      }
    },

    // 获取可用用户列表
    async fetchAvailableUsers() {
      try {
        const data = await getUserList({ limit: 1000 })
        if (data) {
          // 规范化用户列表，确保每个用户对象都有 id 字段 (兼容 userid/id)
          const list = data.list || []
          this.availableUsers = list
            .filter(u => u && String(u.status) !== '0')
            .map(u => ({
            ...u,
            id: u.userid || u.id
            }))
          // 获取用户列表后重新解析所有名称
          this.resolveUserNames()
        }
      } catch (error) {
        console.error('获取用户列表失败:', error)
      }
    },

    // 编辑备注
    handleEditRemark() {
      this.remarkEditing = true
      this.originalRemark = this.deviceDetail.deviceRemark
    },

    // 取消备注编辑
    cancelRemarkEdit() {
      this.remarkEditing = false
      this.deviceDetail.deviceRemark = this.originalRemark
    },

    // 保存备注
    async saveRemark() {
      try {
        await updateDevice(this.deviceDetail.id, {
          deviceRemark: this.deviceDetail.deviceRemark
        })
        this.$message.success(this.$t('common.saveSuccess'))
        this.remarkEditing = false
      } catch (error) {
        console.error('保存备注失败:', error)
        this.$message.error(this.$t('common.saveFailed'))
      }
    },

    // 刷新设备状态
    async refreshStatus() {
      await this.fetchDeviceDetail()
      this.$message.success(this.$t('common.refreshSuccess'))
    },
    // 解绑设备
    handleUnbindDevice() {
      if (!this.deviceDetail.bound) {
        this.$message.warning('该设备当前未绑定，无法解绑')
        return
      }

      this.$confirm(
        `确定要解绑设备"${this.deviceDetail.deviceName || this.deviceDetail.deviceCode}"吗？解绑后该设备将从列表中移除。`,
        this.$t('common.confirm'),
        {
          confirmButtonText: this.$t('common.confirm'),
          cancelButtonText: this.$t('common.cancel'),
          type: 'warning'
        }
      ).then(async () => {
        try {
          const deviceCode = this.deviceDetail.deviceCode
          const userId = this.deviceDetail.userId || this.deviceDetail.user_id
          if (deviceCode) {
            await unbindDeviceByCode({ deviceCode, userId })
          } else {
            this.$message.error('缺少设备编码，无法解绑')
            return
          }
          this.$message.success('设备解绑成功')
          await this.fetchDeviceDetail()
        } catch (error) {
          console.error('设备解绑失败:', error)
          const errorMsg = error.response && error.response.data && error.response.data.msg 
            ? error.response.data.msg 
            : (error.message || '解绑失败')
          this.$message.error(errorMsg)
        }
      }).catch(() => {})
    },

    // 固件升级
    handleFirmwareUpgrade() {
      this.firmwareDialog.visible = true
      this.firmwareForm.targetVersion = ''
      this.firmwareForm.notes = ''
      this.firmwareDialog.progress = 0
    },

    // 执行固件升级
    async executeFirmwareUpgrade() {
      if (!this.firmwareForm.targetVersion) {
        this.$message.warning(this.$t('device.selectVersion'))
        return
      }

      this.firmwareDialog.loading = true
      try {
        await upgradeFirmware(this.deviceDetail.id, this.firmwareForm.targetVersion)
        this.$message.success(this.$t('device.upgradeStart'))
        this.firmwareDialog.loading = false
        this.startUpgradeProgress()
      } catch (error) {
        console.error('固件升级失败:', error)
        this.$message.error(this.$t('device.upgradeFailed'))
        this.firmwareDialog.loading = false
      }
    },

    // 开始升级进度监控
    async startUpgradeProgress() {
      this._upgradeTimer = setInterval(async () => {
        try {
          const data = await getFirmwareProgress(this.deviceDetail.id)
          if (data) {
            this.firmwareDialog.progress = data.progress
            if (data.progress >= 100) {
              clearInterval(this._upgradeTimer)
              this._upgradeTimer = null
              this.$message.success(this.$t('device.upgradeSuccess'))
              this.firmwareDialog.visible = false
              this.fetchDeviceDetail()
            }
          }
        } catch (error) {
          console.error('获取升级进度失败:', error)
        }
      }, 2000)
    },

    // 查看设备日志
    handleViewLogs() {
      this.logsDialog.visible = true
      this.logsForm.logType = ''
      this.logsForm.dateRange = []
      this.fetchDeviceLogs()
    },

    // 获取设备日志
    async fetchDeviceLogs() {
      this.logsDialog.loading = true
      try {
        const params = {
          logType: this.logsForm.logType,
          startDate: this.logsForm.dateRange[0],
          endDate: this.logsForm.dateRange[1],
          page: this.logsPagination.currentPage,
          limit: this.logsPagination.pageSize
        }
        const data = await getDeviceLogs(this.deviceDetail.id, params)
        if (data) {
          this.deviceLogs = data.list || []
          this.logsPagination.total = data.total || 0
        }
      } catch (error) {
        console.error('获取设备日志失败:', error)
        this.$message.error(this.$t('device.getLogsFailed'))
      } finally {
        this.logsDialog.loading = false
      }
    },

    // 重置日志查询表单
    resetLogsForm() {
      this.logsForm.logType = ''
      this.logsForm.dateRange = []
      this.logsPagination.currentPage = 1
      this.fetchDeviceLogs()
    },

    // 日志分页大小变化
    handleLogsSizeChange(val) {
      this.logsPagination.pageSize = val
      this.logsPagination.currentPage = 1
      this.fetchDeviceLogs()
    },

    // 日志页码变化
    handleLogsCurrentChange(val) {
      this.logsPagination.currentPage = val
      this.fetchDeviceLogs()
    }
  }
}
</script>

<style scoped>
.device-detail-container {
  margin: -20px;
  padding: 20px;
  background-color: #f5f7fa;
  height: calc(100% + 40px);
  overflow-y: auto;
  overflow-x: hidden;
  box-sizing: border-box;

  /* 隐藏滚动条 */
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: none;  /* Firefox */

  :deep(.el-table__body-wrapper) {
    &::-webkit-scrollbar {
      display: none;
    }
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
}

.control-panel {
  flex-shrink: 0;
  margin-bottom: 20px;
  background: #fff;
  padding: 15px 20px;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
}

.control-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-info {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.header-info i {
  color: #409eff;
  font-size: 20px;
}

.detail-content {
  padding: 0;
}

.detail-card {
  margin-bottom: 24px;
  border-radius: 16px;
  border: 1px solid #f0f2f5;
  overflow: hidden;

  &:hover {
    box-shadow: 0 8px 24px rgba(0,0,0,0.08) !important;
  }

  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    min-height: 32px;
    font-weight: 800;
    font-size: 17px;
    color: #1e293b;
    padding: 6px 0;
    border-left: 4px solid #409eff;
    padding-left: 12px;
    margin-left: -20px;

    span > i {
      margin-right: 8px;
      color: #409eff;
      font-size: 19px;
      vertical-align: middle;
    }

    .el-button {
      padding: 4px 10px;
    }
  }
}

.info-value {
  color: #606266;
}

.info-value.highlighted {
  color: #303133;
  font-weight: 600;
  font-family: 'Courier New', Courier, monospace;
}

.text-secondary {
  color: #909399;
  font-size: 13px;
}

.sub-title {
  font-size: 14px;
  font-weight: 600;
  color: #606266;
  margin-bottom: 15px;
  display: flex;
  align-items: center;
}

.sub-title::before {
  content: '';
  display: inline-block;
  width: 3px;
  height: 14px;
  background-color: #409eff;
  margin-right: 8px;
  border-radius: 2px;
}

.status-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.status-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 12px;
  border-bottom: 1px dashed #ebeef5;
}

.status-item:last-child {
  border-bottom: none;
}

.status-item .label {
  color: #909399;
  font-size: 14px;
}

.status-item .value {
  color: #303133;
  font-weight: 500;
  font-size: 14px;
}

.operation-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.op-btn {
  margin: 0 !important;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 10px;
}

.remark-text {
  color: #606266;
  font-size: 14px;
  line-height: 1.6;
  background-color: #f8f9fb;
  padding: 12px;
  border-radius: 4px;
  min-height: 80px;
}

.mt-10 { margin-top: 10px; }
.mt-20 { margin-top: 20px; }
.remark-actions { text-align: right; }

.upgrade-progress {
  margin-top: 20px;
  padding: 15px;
  background-color: #f0f9eb;
  border-radius: 4px;
}

.firmware-upgrade-content p {
  margin-bottom: 20px;
  color: #606266;
}

.pagination-container {
  margin-top: 20px;
  text-align: right;
}

@media screen and (max-width: 1200px) {
  .operation-grid {
    grid-template-columns: 1fr;
  }
}

@media screen and (max-width: 768px) {
  .detail-content {
    padding: 10px;
  }

  .header-info span {
    display: none;
  }
}

.volume-display {
  display: flex;
  align-items: center;
  gap: 8px;
}

.volume-label {
  font-size: 13px;
  color: #606266;
  font-weight: 500;
}
</style>
